import { useCallback, useEffect, useRef } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'

import { useToast } from '@/components/ui/use-toast'
import { createVideo, listVideos, deleteVideo } from '@/lib/api/client'
import { uploadFile } from '@/lib/api/client'
import { DocumentPage } from '@/lib/api/client'
import { truncate } from '@/lib/truncate'
import queryClient from '@/queryClient'
import {
  getVideoTemplatesQuery,
  useCreateVideoTemplate,
  useDeleteVideoTemplate,
} from '@/services/hooks/template_attributes'
import MUTATION_KEYS from './mutationKeys'
import QUERY_KEYS from './queryKeys'
import CustomError from '@/lib/api/CustomError'
import { useVideoThumbnails } from './hooks'

/*
 ** Videos in cache need to be updated after 10 minutes
 ** of inactivity to update the download URL
 */
const VIDEOS_CACHE_TIME = 10 * 60 * 1000 // minutes * seconds * milliseconds

export const useCreateVideo = (props?: { onSuccess?: () => void }) => {
  const { onSuccess } = props || {}
  const { mutateAsync: createVideoTemplate } = useCreateVideoTemplate()
  const { toast } = useToast()

  return useMutation({
    mutationKey: [MUTATION_KEYS.CREATE_VIDEO],
    mutationFn: async ({
      documentPage,
      file,
    }: {
      documentPage: DocumentPage
      file: File
      position: {
        clientX: number
        clientY: number
      }
    }) => {
      const documentPageId = documentPage.id
      if (!documentPageId) {
        throw new Error('No document ID found')
      }

      const documentVersionId = documentPage.document_version
      const video = await createVideo(documentVersionId, {
        name: truncate(file.name),
      })

      if (!video.upload_url || !video.id) {
        throw new Error('Unable to create video')
      }

      await createVideoTemplate({
        documentPageId,
        videoData: {
          videoId: video.id as string,
        },
      })

      await uploadFile(video.upload_url, file)

      return {
        documentPage,
        video,
      }
    },
    onSuccess: ({ documentPage, video }) => {
      queryClient.setQueriesData(
        {
          queryKey: [
            QUERY_KEYS.VIDEOS,
            { documentVersionId: documentPage.document_version },
          ],
        },
        (videos: Array<any> | undefined) => {
          if (videos) {
            return [...videos, video]
          }

          return [video]
        },
      )

      if (typeof onSuccess === 'function') {
        onSuccess()
      }
    },
    onError: (error: any) => {
      if (error instanceof CustomError) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        })
      }
    },
  })
}

export const useListVideos = ({
  documentVersionId,
}: {
  documentVersionId: string
}) => {
  const { toast } = useToast()
  const refetchIntervalRef = useRef<number | null>(null)
  const { refetch, data, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.VIDEOS, { documentVersionId }],
    staleTime: VIDEOS_CACHE_TIME,
    queryFn: () => {
      try {
        return documentVersionId ? listVideos(documentVersionId) : []
      } catch (error) {
        console.error(error)
        if (error instanceof CustomError) {
          toast({
            title: 'Error',
            description: error.message,
            variant: 'destructive',
          })
        } else {
          toast({
            variant: 'destructive',
            title: 'Uh oh! Something went wrong.',
            description: 'There was a problem with fetching videos.',
          })
        }
        return []
      }
    },
    enabled: !!documentVersionId,
  })

  const startRefetchInterval = useCallback(() => {
    refetchIntervalRef.current = window.setInterval(() => {
      void refetch()
    }, VIDEOS_CACHE_TIME - 100)
  }, [refetch])

  const refetchQuery = useCallback(() => {
    if (refetchIntervalRef.current) {
      clearInterval(refetchIntervalRef.current)
    }
    startRefetchInterval()
  }, [startRefetchInterval])

  useEffect(() => {
    const refetchIntervalId = refetchIntervalRef.current
    if (data?.length) {
      startRefetchInterval()
    } else {
      if (refetchIntervalId) clearInterval(refetchIntervalId)
    }
    return () => {
      if (refetchIntervalId) clearInterval(refetchIntervalId)
    }
  }, [startRefetchInterval, data])

  return {
    ...rest,
    data,
    refetch: refetchQuery,
  }
}

export const useListEnrichedVideos = ({
  documentVersionId,
}: {
  documentVersionId: string
}) => {
  const { data: videos, ...rest } = useListVideos({ documentVersionId })
  const enrichedVideos = useVideoThumbnails(videos, 1)
  return {
    data: enrichedVideos?.length
      ? enrichedVideos
      : (videos || []).map((video) => ({
          ...video,
          download_url: null,
          loading: true,
        })),
    ...rest,
  }
}

export const useDeleteVideo = (props?: { onDelete?: () => void }) => {
  const { onDelete } = props || {}
  const { toast } = useToast()
  const { mutateAsync: deleteVideoTemplate } = useDeleteVideoTemplate()

  return useMutation({
    mutationKey: [MUTATION_KEYS.DELETE_VIDEO],
    mutationFn: async ({
      videoId,
      documentPage,
    }: {
      videoId: string
      documentPage: DocumentPage
    }) => {
      const documentPageId = documentPage.id
      if (!documentPageId) {
        return { documentVersionId: documentPage.document_version }
      }

      await deleteVideo(videoId)

      const videoTemplates = await getVideoTemplatesQuery(documentPageId)
      const video = videoTemplates.find(
        (template) => template.template_values.videoId === videoId,
      )

      if (!video) return { documentVersionId: documentPage.document_version }

      await deleteVideoTemplate(video.id as string)

      return {
        documentVersionId: documentPage.document_version,
      }
    },
    onSuccess: async ({ documentVersionId }) => {
      await queryClient.invalidateQueries({
        queryKey: [
          QUERY_KEYS.VIDEOS,
          documentVersionId ? { documentVersionId } : {},
        ],
      })
      if (typeof onDelete === 'function') {
        onDelete()
      }
    },
    onError: (error: any) => {
      if (error instanceof CustomError) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        })
      }
    },
  })
}
